// Pixel-perfect KidPath landing markup (ported from KidPath_landing.html).
// Rendered via dangerouslySetInnerHTML so the original class-based CSS applies unchanged.
export const LANDING_HTML = `
<div class="announce" data-testid="landing-announcement">Now starting with <b>Ontario support pathways</b> — built to grow with families across Canada.</div>

<header class="nav" id="nav">
  <div class="wrap nav-inner">
    <a class="brand" href="/" aria-label="KidPath home"><img src="/assets/kidpath-mark-light.png" alt=""><span><span class="k">Kid</span><span class="p">Path</span></span></a>
    <nav class="nav-links" id="navlinks" aria-label="Primary">
      <a href="#how">How it works</a>
      <a href="/providers">Find a Provider</a>
      <a href="#pricing">Pricing</a>
      <a href="/articles">Articles</a>
    </nav>
    <div class="nav-cta">
      <a class="signin" href="/login" data-testid="nav-signin-link">Sign in</a>
      <a class="btn btn-primary" href="/register" data-testid="nav-start-free-btn">Start free</a>
      <button class="menu-btn" id="menuBtn" aria-label="Menu" aria-expanded="false"><span></span><span></span><span></span></button>
    </div>
  </div>
</header>
<div class="nav-scrim" id="navscrim" aria-hidden="true"></div>

<main id="top">
<section class="hero" aria-labelledby="h1">
  <div class="wrap">
    <div class="hero-copy">
      <span class="eyebrow">For Canadian autism families</span>
      <h1 id="h1">A <span class="uline">calmer way</span> to navigate autism funding&nbsp;&amp; support.</h1>
      <p class="lede">KidPath helps parents and caregivers understand available funding, organize next steps, and find support options for their autistic child — without having to figure it all out alone.</p>
      <div class="hero-cta">
        <a class="btn btn-primary btn-lg" href="/register" data-testid="hero-get-roadmap-btn">Start your free support check <span class="arr">→</span></a>
        <a class="btn btn-ghost btn-lg" href="#how" data-testid="hero-how-btn">See how it works</a>
      </div>
      <div class="muted-sm" style="margin:-4px 0 14px">Takes about 2 minutes · No credit card required.</div>
      <div class="trust"><b>Funding guidance</b> · Step-by-step support planning · Private &amp; secure</div>
      <div class="reassure">
        <svg class="arrowpath" viewBox="0 0 40 24" fill="none"><circle cx="5" cy="20" r="1.7" fill="var(--teal)"/><circle cx="10.5" cy="14.5" r="1.7" fill="var(--teal)"/><circle cx="16.5" cy="10.5" r="1.7" fill="var(--teal)"/><circle cx="23" cy="8.6" r="1.7" fill="var(--teal)"/><path d="M26.5,4 L33.5,9 L26.5,14" stroke="var(--teal)" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
        You are not alone. There is a path forward.
      </div>
    </div>
    <div class="hero-media">
      <div class="backblob"></div>
      <div class="confetti" aria-hidden="true"><span></span><span></span><span></span><span></span><span></span></div>
      <div class="blob-frame"><img src="/assets/hero.jpg" alt="A parent and child sharing a calm, everyday moment at a bright kitchen table."></div>
      <div class="roadmap-card" role="img" aria-label="Family support path preview showing 4 of 7 steps complete">
        <div class="rc-head">
          <div class="ring" style="--p:57"><b>4/7</b></div>
          <div><div class="t">Your support path</div><div class="s">Guided · updated monthly</div></div>
        </div>
        <div class="rc-row"><i style="background:var(--green)">✓</i><span>Disability Tax Credit — worth reviewing</span></div>
        <div class="rc-row"><i style="background:var(--sun)">✓</i><span>School meeting prep pack</span></div>
        <div class="rc-row"><i style="background:var(--sky)">3</i><span>Documents to prepare</span></div>
      </div>
    </div>
  </div>
</section>

<div class="strip">
  <div class="cf" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
  <div class="wrap">
    <b>You know your child. We help you navigate the system around them.</b>
    <span class="muted-sm">Funding, applications, waitlists, school conversations and appointments — brought together in one calm place.</span>
  </div>
</div>

<section id="how">
  <div class="wrap">
    <div class="section-head">
      <span class="eyebrow">How it works</span>
      <h2>A support path built around your family.</h2>
      <p>Answer a few simple questions and KidPath helps create a personalized support path based on your child's age, your current stage, your funding status, and what your family needs help with right now.</p>
    </div>
    <div class="steps-wrap">
      <svg class="steps-path" viewBox="0 0 1000 70" preserveAspectRatio="none" aria-hidden="true">
        <path d="M20,35 C220,-10 340,72 500,35 C660,-2 780,72 980,35" fill="none" stroke="#CBE7F6" stroke-width="3.5" stroke-linecap="round" stroke-dasharray="1 14"/>
      </svg>
      <div class="steps">
        <div class="step"><div class="num">1</div><h3>Tell us about your family</h3><p>A short, plain-language intake — your child's age, province, stage and priorities. A few minutes, no jargon.</p></div>
        <div class="step"><div class="num">2</div><h3>Get your personalized support path</h3><p>Funding to review, tax credits, school supports and the documents to prepare — organized just for you.</p></div>
        <div class="step"><div class="num">3</div><h3>Take the next step, guided</h3><p>Plain-language help, checklists and gentle reminders. Pause and come back any time.</p></div>
      </div>
    </div>
  </div>
</section>

<section id="what" class="tint">
  <div class="wrap">
    <div class="section-head">
      <span class="eyebrow">What you get</span>
      <h2>Built to make the next step easier.</h2>
      <p>Simple tools that carry you from "where do we start?" to "we've got this."</p>
    </div>
    <div class="mods">
      <div class="mod wide" style="background:#E7F6F3">
        <div class="ic" style="background:var(--teal)"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 18c4-6 12 2 16-8"/><circle cx="4" cy="18" r="1.8"/><circle cx="12" cy="14" r="1.8"/><circle cx="20" cy="10" r="1.8"/></svg></div>
        <div>
          <h3>Support roadmap</h3>
          <p>Your whole plan in one place — programs to review, tax credits, school supports and documents to prepare, ordered into clear next steps just for your family.</p>
          <span class="chip"><span class="dots"><i style="background:var(--green)"></i><i style="background:var(--sun)"></i><i style="background:var(--sky)"></i></span> 4 of 7 steps · guided</span>
        </div>
      </div>
      <div class="mod" style="background:#EAF5FD">
        <div class="ic" style="background:var(--sky)"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></svg></div>
        <h3>Family dashboard</h3><p>Keep applications, documents, notes, appointments and next steps organized in one calm place.</p>
      </div>
      <div class="mod" style="background:#FFF6E3">
        <div class="ic dark" style="background:var(--sun)"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5c-1.6 0-2.6.9-2.6 2.1s1 1.7 2.6 2.1 2.6.9 2.6 2.1-1 2.1-2.6 2.1M12 6v12"/></svg></div>
        <h3>Funding guidance</h3><p>Understand available funding pathways, eligibility basics, application steps, and how different supports may fit together.</p>
      </div>
      <div class="mod" style="background:#F1ECFB">
        <div class="ic" style="background:var(--grape)"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h16v10H9l-4 4z"/><path d="M8 10h8M8 13h5"/></svg></div>
        <h3>Plain-language support</h3><p>KidPath translates confusing program information into clear, practical guidance — and helps you prepare questions and school emails.</p>
      </div>
      <div class="mod" style="background:#EAF7EF">
        <div class="ic" style="background:var(--green)"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7v12h16v-9h-8l-2-3z"/><path d="M9 14h6"/></svg></div>
        <h3>Guided checklists</h3><p>Know what to prepare before calls, applications, assessments or school meetings.</p>
      </div>
      <div class="mod full" style="background:linear-gradient(120deg,#EAF6FD,#E6F6F2 60%,#F3F0FF)">
        <div class="ic onwhite" style="background:#fff;box-shadow:var(--shadow-sm)"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.4-7 10-7 10z"/></svg></div>
        <div><h3>Built with care</h3><p>Warm, careful and inclusive — designed with Canadian parents, OTs and clinicians, and written in plain language you can actually use.</p></div>
      </div>
    </div>
  </div>
</section>

<section>
  <div class="wrap">
    <div class="careful">
      <div class="cf" aria-hidden="true"><span></span><span></span><span></span></div>
      <div class="care-content">
        <span class="eyebrow" style="background:rgba(127,209,203,.14);border-color:transparent;color:#7FD1CB">Careful by design</span>
        <h2>Support navigation should feel clearer than this.</h2>
        <p>Families are often expected to become experts in funding rules, service options and application requirements overnight. KidPath helps reduce the guesswork by organizing the information families need most.</p>
        <ul class="care-list">
          <li><span class="chk">✓</span> What funding or programs may be relevant.</li>
          <li><span class="chk">✓</span> What steps to take now, and what to prepare.</li>
          <li><span class="chk">✓</span> What questions to ask before making decisions.</li>
          <li><span class="chk">✓</span> Your family's information stays private, secure and yours.</li>
        </ul>
      </div>
      <div class="care-photo">
        <span class="cfdot a"></span><span class="cfdot b"></span>
        <div class="frame"><img src="/assets/careful.jpg" alt="A parent taking a calm, reassured moment while sorting out their family's plan."></div>
      </div>
    </div>
  </div>
</section>

<section class="tint">
  <div class="wrap">
    <div class="band">
      <div class="photo">
        <div class="cf" aria-hidden="true"><span></span><span></span></div>
        <div class="frame"><img src="/assets/band.jpg" alt="A family walking together along a sunlit path outdoors."></div>
      </div>
      <div>
        <span class="eyebrow">Why KidPath</span>
        <h2 style="margin-top:18px">Better information can make the path feel <span class="uline sun">lighter</span>.</h2>
        <p>Families shouldn't have to search across dozens of websites, save random screenshots, and wonder whether they missed something important. KidPath brings structure to the process so you can spend less time decoding the system and more time focusing on your child and family.</p>
        <div class="quote">"I just wanted to know what to do next. KidPath made the process feel less overwhelming."</div>
      </div>
    </div>
  </div>
</section>

<section id="pricing">
  <div class="wrap">
    <div class="section-head">
      <span class="eyebrow">Simple pricing</span>
      <h2>Start free. Upgrade only when it helps.</h2>
      <p>No pressure, no lock-in. Change or cancel any time.</p>
    </div>
    <div class="plans">
      <div class="plan">
        <div class="name">Free Plan</div>
        <div class="price">Free</div>
        <p class="muted-sm">For families who want a clearer starting point.</p>
        <ul>
          <li><span class="c">✓</span> Free support check</li>
          <li><span class="c">✓</span> Basic funding guidance</li>
          <li><span class="c">✓</span> Helpful next steps</li>
          <li><span class="c">✓</span> Family support checklist</li>
        </ul>
        <a class="btn btn-ghost" href="/register" data-testid="pricing-free-btn">Start free</a>
      </div>
      <div class="plan featured">
        <span class="tag">Most families</span>
        <div class="name">Guided Support Plan</div>
        <div class="price">$19<small>/month</small></div>
        <p class="muted-sm">For families who want more organization and ongoing help. Or save with $169/year.</p>
        <ul>
          <li><span class="c">✓</span> Personalized support path</li>
          <li><span class="c">✓</span> AI family navigator</li>
          <li><span class="c">✓</span> Application &amp; document tracking</li>
          <li><span class="c">✓</span> Saved family profile</li>
          <li><span class="c">✓</span> Reminders &amp; next-step planning</li>
          <li><span class="c">✓</span> Expanded guidance as new supports are added</li>
        </ul>
        <a class="btn btn-primary" href="/register" data-testid="pricing-paid-btn">Start Guided Support <span class="arr">→</span></a>
      </div>
    </div>
    <p class="addon-note">KidPath is designed to stay accessible — start free, and upgrade only when the guided tools genuinely help your family.</p>
  </div>
</section>

<section id="faq" class="tint">
  <div class="wrap">
    <div class="section-head">
      <span class="eyebrow">Good questions</span>
      <h2>Questions families often ask.</h2>
    </div>
    <div class="faq">
      <details open><summary>Is KidPath only for families with a diagnosis?<span class="pl">+</span></summary><p>No. KidPath can help families at different stages, including those exploring concerns, waiting for an assessment, newly navigating a diagnosis, applying for funding, or looking for support.</p></details>
      <details><summary>Is KidPath a therapy provider?<span class="pl">+</span></summary><p>No. KidPath is a support navigation platform. We help families understand options, organize next steps, and find resources to explore.</p></details>
      <details><summary>Does KidPath replace professional advice?<span class="pl">+</span></summary><p>No. KidPath does not replace medical, clinical, educational, legal, financial, or government advice. It helps families prepare, organize, and navigate information more clearly.</p></details>
      <details><summary>Why are you starting in Ontario?<span class="pl">+</span></summary><p>We are starting with Ontario pathways first so the information is accurate and useful at launch. From there, KidPath will expand support for more families across Canada.</p></details>
      <details><summary>What language does KidPath use around autism?<span class="pl">+</span></summary><p>KidPath aims to use respectful, inclusive, neurodiversity-affirming language. We recognize that autistic people and families may have different language preferences, and we do our best to honour that.</p></details>
    </div>
  </div>
</section>

<section id="glossary">
  <div class="wrap">
    <div class="section-head">
      <span class="eyebrow">Plain-language glossary</span>
      <h2>Understand the words before the meetings.</h2>
      <p>IEP, DTC, OAP, ABA… KidPath explains 239 autism, funding and school terms in clear, supportive language — free and open to every family.</p>
    </div>
    <div class="glossary-links" data-testid="landing-featured-terms">
      <a href="/terms/individual-education-plan-iep">Individual Education Plan (IEP)</a>
      <a href="/terms/disability-tax-credit-dtc">Disability Tax Credit (DTC)</a>
      <a href="/terms/ontario-autism-program-oap">Ontario Autism Program (OAP)</a>
      <a href="/terms/applied-behaviour-analysis-aba">Applied Behaviour Analysis (ABA)</a>
      <a href="/terms/occupational-therapy-ot">Occupational Therapy (OT)</a>
      <a href="/terms/autism-assessment">Autism Assessment</a>
      <a href="/terms/child-disability-benefit-cdb">Child Disability Benefit (CDB)</a>
      <a href="/terms/stimming">Stimming</a>
    </div>
    <div class="glossary-cta">
      <a class="btn btn-primary" href="/terms" data-testid="landing-glossary-browse-btn">Browse all 239 terms <span class="arr">→</span></a>
    </div>
  </div>
</section>

<section id="cta">
  <div class="wrap">
    <div class="cta-final">
      <div class="cf" aria-hidden="true"><span></span><span></span><span></span><span></span><span></span></div>
      <img class="mark" src="/assets/kidpath-mark-light.png" alt="">
      <span class="eyebrow">Less overwhelm. More confidence.</span>
      <h2 style="margin-top:16px">Find out what support may be available.</h2>
      <p>Take the first step toward a clearer plan for funding, services, and next steps.</p>
      <a class="btn btn-primary btn-lg" href="/register" data-testid="final-cta-btn">Start your free support check <span class="arr">→</span></a>
      <div class="muted-sm" style="margin-top:14px">Simple, private, and built for families navigating autism support.</div>
    </div>
  </div>
</section>
</main>

<footer>
  <div class="wrap">
    <div class="foot-grid">
      <div class="foot-brand">
        <div class="brand"><img src="/assets/kidpath-mark-dark.png" alt=""><span><span class="k">Kid</span><span class="p">Path</span></span></div>
        <p>General information and support-navigation tools for Canadian families navigating autism support.</p>
      </div>
      <div class="foot-col"><h4>Product</h4><a href="/providers">Find a Provider</a><a href="#how">How it works</a><a href="#what">What you get</a><a href="#pricing">Pricing</a><a href="/register">Support path</a></div>
      <div class="foot-col"><h4>Resources</h4><a href="/funding-guide">Free Funding Guide</a><a href="/articles">Articles</a><a href="/terms">Glossary</a><a href="#faq">Getting started</a><a href="#faq">FAQ</a></div>
      <div class="foot-col"><h4>Company</h4><a href="/about">About</a><a href="/contact">Contact</a><a href="/privacy">Privacy</a><a href="/terms-of-service">Terms</a><a href="/terms">Glossary</a></div>
    </div>
    <div class="foot-bottom">
      <span>© 2026 KidPath · Made in Canada 🍁</span>
      <span>KidPath.ca</span>
    </div>
  </div>
  <p class="disclaimer">KidPath provides general information and support navigation tools. It does not provide medical, clinical, legal, financial, government, or funding eligibility advice. Program details, eligibility rules, and service options may change. Families should confirm details with official sources and qualified professionals.</p>
</footer>
`;
